import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTable, useFilters, useGlobalFilter, usePagination, useSortBy } from "react-table";
import Swal from "sweetalert2";
import { FaSearch, FaFileDownload } from "react-icons/fa";
import { HiRefresh } from "react-icons/hi";
// import Table from "../pages/ReactTable"
import Loader from "../component/Loader";
import "../css/makeTable.css";
import { getData } from "../store/utils";
import DatePicker from "react-datepicker";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { useDebounce } from 'use-debounce';
import Select from "react-select";
import { formatDate, formatDateTimeSecond, formatDateString } from "../component/common";

const UwDashboard = () => {
  const inputRef = useRef();
  const [isLoadingOn, setIsLoadingOn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [records, setRecords] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState(""); 
  const [orderBy, setOrderBy] = useState("");
  const [searchBoxValue, setSearchBoxValue] = useState("");
  const [queryParams, setQueryParams] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [todayDate, setTodayDate] = useState(null);
  const [filterData, setFilterData] = useState('');
  const [value] = useDebounce(searchBoxValue, 1000);

  const statusOption = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'currentWeek', label: 'Current Week' },    
    { value: 'lastWeek', label: 'Last Week' }, 
    { value: 'currentMonth', label: 'Current Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'overall', label: 'Overall' }, 
  ];

  
  const getBrandsData = async () => {
    if (isLoadingOn) return;
    setIsLoadingOn(true);
    const today = format(new Date(), "yyyy-MM-dd");
    setTodayDate(today);
    
    let computedFromDate = fromDate;
    let computedToDate = toDate;
        
    switch (filterData) {
        case "today":
            computedFromDate = today;
            computedToDate = today;
            break;
        case "yesterday":
            computedFromDate = format(subDays(new Date(), 1), "yyyy-MM-dd");
            computedToDate = computedFromDate;
            break;
        case "currentWeek":
            computedFromDate = format(startOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd");
            computedToDate = format(endOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd");
            break;
        case "lastWeek":
            computedFromDate = format(startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }), "yyyy-MM-dd");
            computedToDate = format(endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }), "yyyy-MM-dd");
            break;
        case "currentMonth":
            computedFromDate = format(startOfMonth(new Date()), "yyyy-MM-dd");
            computedToDate = format(endOfMonth(new Date()), "yyyy-MM-dd");
            break;
        case "lastMonth":
            computedFromDate = format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
            computedToDate = format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
            break;
        case "overall":
            computedFromDate = null;
            computedToDate = null;
            break;
    }
        
    let queryParams = new URLSearchParams();
    
    queryParams.append("page", (pageIndex + 1).toString());
    queryParams.append("pageSize", pageSize.toString());
    
    if (computedFromDate) queryParams.append("fromDate", computedFromDate);
    if (computedToDate) queryParams.append("toDate", computedToDate);
    
    if (sortBy && orderBy) {
        queryParams.append("sortBy", sortBy);
        queryParams.append("orderBy", orderBy);
    }
    
    if (searchBoxValue.trim()) {
        queryParams.append("search", searchBoxValue.trim());
    }

    const apiUrl = `dashboard/uwDashboard?${queryParams.toString()}`;

    try {
      const getDataResult = await getData(apiUrl);
      if (getDataResult.statusCode == 200) {
        setRecords(getDataResult.data.records);
        setPageCount(getDataResult.data.pagination.totalPagesCount);
        setTotalRecords(getDataResult.data.pagination.totalRecords);

        if (getDataResult?.data?.pagination?.totalRecords == 0) {
          setErrorMessage("No data found");
        } else {
          setErrorMessage("");
        }
      }
      else {
        Swal.fire({
          title: "Error!",
          text: getDataResult?.response?.data?.errors?.msg || "Something went wrong."
        })
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "An error occurred while fetching data."
      });
    } finally {
      setIsLoadingOn(false);
    }
  };

    // const downloadDumpData = async () => {
    //     setIsLoadingOn(true);
    //     const tempData = await getData("dashboard/getExcelBrandData/nothing");
    //     setIsLoadingOn(false);
    //     if (tempData.statusCode === 200) {
    //         const downloadLink = document.createElement("a");
    //         downloadLink.href = tempData.data.Location;
    //         downloadLink.target = "_blank";
    //         downloadLink.rel = "noopener noreferrer";
    //         downloadLink.click();
    //     }
    // };

    const handleFromDateChange = (date) => {
        const formattedDate = date ? format(date, "yyyy-MM-dd") : null;
        setFromDate(formattedDate);
    };

    const handleToDateChange = (date) => {
        const formattedDate = date ? format(date, "yyyy-MM-dd") : null;
        setToDate(formattedDate);
    };

    const handleGotoPage = (page) => {
        if (page < 0 || page >= pageCount) {
        setErrorMessage(`No data to display`);
        } else {
        setErrorMessage("");
        setPageIndex(page);
        }
    };

    const handleSort = (column) => {
        if (column.disableSortBy) return;
        let newOrderBy = "asc";
    
        if (sortBy === column.id) {
        newOrderBy = orderBy === "asc" ? "desc" : "asc";
        }
        setSortBy(column.id);
        setOrderBy(newOrderBy);
    };
    
    useEffect(() => {
        getBrandsData(pageIndex, pageSize, sortBy, orderBy, queryParams, filterData, fromDate, toDate, value);
    }, [pageIndex, pageSize, sortBy, orderBy, queryParams,filterData, fromDate, toDate, value]);

    const columns = useMemo(
        () => [
            // {
            //     Header: "Sr. No.",
            //     accessor: (row, i) => i + 1 + pageIndex * pageSize,
            //     Cell: ({ cell }) => (
            //     <span style={{ fontWeight: "600",fontSize:"0.875rem" }}>{cell.row.index + 1 + pageIndex * pageSize}.</span>
            //     ),
            //     disableSortBy: true,
            // },
            {
                Header: "User ID",
                accessor: "userId",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Shop Name",
                accessor: "shopName",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Phone Number",
                accessor: "phoneNumber",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Onboarding Status",
                accessor: "onboardingStatus",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Credit Status",
                accessor: "creditStatus",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Credit Limit",
                accessor: "creditLimit",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Used Limit",
                accessor: "usedLimit",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "System Generated Limit",
                accessor: "systemGeneratedLimit",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Evaluation Model Limit",
                accessor: "evaluationModelLimit",
                Cell: ({ value }) => value ?? ""
            },            
            {
                Header: "Risk Classification",
                accessor: "riskClassification",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Bureau Score",
                accessor: "bureauScore",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Most Severe Status (24m)",
                accessor: "mostSevereStatus24m",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Total Balance",
                accessor: "totalBalance",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Bureau Total Credit Limit",
                accessor: "bureauTotalCreditLimit",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "live EMI Amount",
                accessor: "liveEMIAmount",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Number of Accounts",
                accessor: "numberofAccounts",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Active Accounts",
                accessor: "activeAccounts",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Avg Monthly Debit",
                accessor: "avgMonthlyDebit",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Avg Monthly Credit",
                accessor: "avgMonthlyCredit",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Unique Overdue Count",
                accessor: "uniqueOverdueCount",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Total Unique Overdue Amount",
                accessor: "totalUniqueOverdueAmount",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Cheque Bounce Count",
                accessor: "chequeBounceCount",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Bounced Cheque Amount",
                accessor: "bouncedChequeAmount",
                Cell: ({ value }) => value ?? ""
            },
            {
                Header: "Onboarding Date",
                accessor: "onboardingDate",
                Cell: ({ value }) => (value ? formatDateTimeSecond(value) : '')
            },
        ],
        [pageIndex, pageSize]
    );

    const searchManualData = async () => {
        let filterData = {};
        if (searchBoxValue) {
            filterData = {
                brand: searchBoxValue,
            };
        }

        setPageIndex(0);
        const tempQueryParams = new URLSearchParams(filterData).toString();
        setQueryParams(tempQueryParams);
    };

    const resetFilters = () => {
        inputRef.current.value = "";
        setSearchBoxValue("");
        setFromDate(null);
        setToDate(null);
        setQueryParams("");
        setPageIndex(0);
        getBrandsData();
        setFilterData('overall');
    };

    return (
        <>
        {isLoadingOn ? <Loader /> : ""}
        <div className="centre">
            <h4 style={{ textAlign: 'left'}}>UW Dashboard</h4>
        </div>
        <div className="customHeaderTable">
            <div className="container-fluid">
            <div className="row alignmentTable" style={{ position: "relative", zIndex: 3 }}>
                <div className="col-md-2">
                    <Select 
                        defaultValue={statusOption[6]}
                        placeholder="Onboarding Status"
                        name="role"
                        options={statusOption}
                        onChange={(e) => setFilterData(e.value)}
                    />
                </div>
                <div className="col-md-2">
                    <input
                        type="text"
                        className="searchInputField w-100"
                        ref={inputRef}
                        onChange={(e) => setSearchBoxValue(e.target.value)}
                        placeholder="Enter Search"
                        value={searchBoxValue}
                    />
                </div>
                <div className="col-md-3">
                    <label>From :</label>&nbsp;
                    <DatePicker
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        selected={fromDate ? new Date(fromDate) : null}
                        onChange={handleFromDateChange}
                        placeholderText="Select Start Date"
                        maxDate={new Date()}
                    />
                </div>
                <div className="col-md-3">
                    <label>To :</label>&nbsp;
                    <DatePicker
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        selected={toDate ? new Date(toDate) : null}
                        onChange={handleToDateChange}
                        placeholderText="Select End Date"
                        maxDate={new Date()}
                    />
                </div>
                {/* <div className="col-md-1">
                <button
                    type="button"
                    className="searchTableData"
                    onClick={() => {
                        setPageIndex(0);
                        getBrandsData();
                    }}                >
                    <FaSearch /> Search
                </button>
                </div> */}
                <div className="col-md-2 text-center" onClick={resetFilters}>
                    <button className="resetTableData">
                        <HiRefresh /> Reset
                    </button>
                </div>
                {/* <div className="col-md-1 pl-0 text-center">
                    <button
                        onClick={downloadDumpData}
                        id="CSV"
                        type="button"
                        className="font-weight-bold download-csv transformBtn acceptDataCss"
                    >
                    <a href="javascript:void(0)">
                        <FaFileDownload />
                    </a>
                    </button>
                </div> */}
            </div>
            </div>
        </div>
        <div className="customHeaderTable">
            {records && <Table 
            columns={columns} 
            data={records} 
            pageCount={pageCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPageIndex={setPageIndex}
            setPageSize={setPageSize}
            totalRecords={totalRecords}
            handleGotoPage={handleGotoPage}
            errorMessage={errorMessage} 
            handleSort={handleSort}
            sortBy={sortBy}
            orderBy={orderBy}
            />}
        </div>
        </>
    );
};

export default UwDashboard;

function Table({  
    columns, 
    data,
    pageCount,
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    totalRecords,
    handleGotoPage,
    errorMessage, 
    handleSort,
    sortBy,
    orderBy
}) {
    const filterTypes = React.useMemo(
        () => ({
            text: (rows, id, filterValue) => {
                return rows.filter((row) => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined
                        ? String(rowValue)
                            .toLowerCase()
                            .startsWith(String(filterValue).toLowerCase())
                        : true;
                });
            },
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        state: { pageIndex: tablePageIndex, pageSize: tablePageSize },
    } = useTable(
        {
            columns,
            data,
            filterTypes,
            manualPagination: true,
            pageCount,
            initialState: { pageIndex, pageSize },
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const handleNextPage = () => {
        if (canNextPage) {
            setPageIndex(pageIndex + 1);
        }
    };
    const handlePreviousPage = () => {
        if (canPreviousPage) {
            setPageIndex(pageIndex - 1);
        }
    };
    const handleSetPageSize = (size) => {
        setPageSize(size);
        setPageIndex(0);
    };

    return (
        <>
            <style>
                {`
                    .table-container {
                        overflow-x: auto; /* Enable horizontal scrolling */
                        max-width: 100%;
                        border: 0;
                        padding: 0;
                        position: relative;
                    }

                    /* Table */
                    .dashboardTable {
                        width: 100%;
                        border-collapse: collapse;
                        border-radius: 8px;
                    }

                    /* Table Header (Sticky) */
                    .dashboardTable thead {
                        position: sticky;
                        top: 0;
                        z-index: 2;
                        background-color: #f4f4f4;
                    }

                    .dashboardTable th {
                        position: sticky;
                        top: 0;
                        background-color: #f4f4f4;
                        font-weight: bold;
                        font-family: Roboto, Helvetica, Arial, sans-serif;
                        font-size: 0.875rem;
                        line-height: 2;
                        letter-spacing: 0.01071em;
                        border-bottom: 1px solid rgb(224, 224, 224);
                        color: rgba(0, 0, 0, 0.87);
                        padding: 20px;
                        height: 100px;  /* Explicitly setting row height */
                        text-align: left;
                        white-space: nowrap;
                    }

                    /* Sticky First Column */
                    .dashboardTable th.frozen-column:first-child,
                    .dashboardTable td.frozen-column:first-child {
                        position: sticky;
                        left: 0;
                        z-index: 1;
                        background-color: white;
                        border-right: 1px solid #ddd;
                    }

                    /* Sticky Second Column */
                    .dashboardTable th.frozen-column:nth-child(2),
                    .dashboardTable td.frozen-column:nth-child(2) {
                        position: sticky;
                        left: 52px;
                        z-index: 1;
                        background-color: white;
                        border-right: 1px solid #ddd;
                    }

                    /* Table Cells */
                    .dashboardTable td {
                        white-space: nowrap;
                        font-family: Roboto, Helvetica, Arial, sans-serif;
                        font-weight: 400;
                        font-size: 0.875rem;
                        line-height: 1.43;
                        letter-spacing: 0.01071em;
                        vertical-align: inherit;
                        border-bottom: 1px solid rgb(224, 224, 224);
                        text-align: left;
                        padding: 16px;
                        color: rgba(0, 0, 0, 0.87);
                    }

                    /* Pagination */
                    .pagination {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 20px;
                    }

                    .pagination button {
                        padding: 5px 10px;
                        margin-right: 5px;
                        cursor: pointer;
                    }

                    .pagination select,
                    .pagination input {
                        padding: 5px;
                        margin-left: 10px;
                    }

                    .selectDisplayNoOfRecords {
                        width: 120px;
                    }

                    .gotoPage {
                        width: 60px;
                    }
                `}
            </style>

            <div className="table-container">
                <table {...getTableProps()} className="dashboardTable">
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()} className="tableHeader">
                                {headerGroup.headers.map((column, index) => (
                                    <th
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        onClick={() => handleSort(column)}
                                        className={index < 2 ? 'frozen-column' : ''}
                                    >
                                        {column.render('Header')}
                                        <span>
                                            {column.id === sortBy && (orderBy === 'asc' ? '↑' : '↓')}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {errorMessage ? (
                            <tr style={{ height: 150 }}>
                                <td colSpan={columns.length} style={{ textAlign: 'center', color: 'salmon' }}>
                                    <h4><strong>{errorMessage}</strong></h4>
                                </td>
                            </tr>
                        ) : (
                            page.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell, index) => (
                                            <td {...cell.getCellProps()} className={`tableTd ${index < 2 ? 'frozen-column' : ''}`}>  {/* Apply sticky class for first two columns */}
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <button onClick={() => handleGotoPage(0)} disabled={tablePageIndex === 0}>{"<<"}</button>
                <button onClick={handlePreviousPage} disabled={!canPreviousPage}>{"<"}</button>
                <button onClick={handleNextPage} disabled={!canNextPage}>{">"}</button>
                <button onClick={() => handleGotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>

                <span>
                    Total Records: <strong>{totalRecords}</strong>
                </span>
                <span>
                    Page {tablePageIndex + 1} of {pageCount}
                </span>

                <div className="gotoPageContainer">
                    <span>Go to page</span>
                    <input
                        type="number"
                        defaultValue={tablePageIndex + 1}
                        onChange={(e) => handleGotoPage(Number(e.target.value) - 1)}
                    />
                </div>

                <select
                    value={pageSize}
                    onChange={(e) => handleSetPageSize(Number(e.target.value))}
                    className="selectDisplayNoOfRecords"
                >
                    {[10, 20, 30, 50].map((size) => (
                        <option key={size} value={size}>Show {size}</option>
                    ))}
                </select>
            </div>
        </>
    );
}
